import type { RouteConfig } from '@/config/routes';

import { hasPermission, Scope } from '@/utils/permissions';

/**
 * Filters routes based on user permissions.
 * Only returns routes where the user has ALL required permissions.
 *
 * @param routes - Array of route configurations
 * @param scope - User's permission scope
 * @returns Filtered array of routes user can access
 */
const isDev =
  typeof (globalThis as Record<string, unknown>).process !== 'undefined' &&
  ((globalThis as Record<string, unknown>).process as { env?: { NODE_ENV?: string } }).env
    ?.NODE_ENV === 'development';

export function filterRoutesByPermissions<T extends RouteConfig>(
  routes: readonly T[],
  scope: Scope,
): T[] {
  if (!scope) {
    return [];
  }

  return routes.filter((route) => {
    // Check all required permissions for this route
    return route.requiredPermissions.every((permission) => {
      const [resource, action, requiredScope] = permission.split('.');

      if (!resource || !action || !requiredScope) {
        if (isDev) {
          console.error(
            `Invalid permission format: ${permission}. Expected "resource.action.scope"`,
          );
        }
        return false;
      }

      return hasPermission(scope, resource, action, requiredScope);
    });
  });
}
